import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../constants';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  ChartDataLabels
);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const TOKSubpageView = () => {
  // State for data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filters
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedSubpage, setSelectedSubpage] = useState('');
  
  // State for unique values
  const [brands, setBrands] = useState([]);
  const [positions, setPositions] = useState([]);
  const [availableSubpages, setAvailableSubpages] = useState([]);
  
  // State for subpage details
  const [subpageDetails, setSubpageDetails] = useState(null);

  // Fetch available subpages
  useEffect(() => {
    const fetchSubpages = async () => {
      try {
        const response = await axios.get(`${API_URL}/toc-kpi/subpages`);
        const subpages = response.data.subpages;
        setAvailableSubpages(subpages);
        
        // Set the first subpage as default if none selected
        if (subpages.length > 0 && !selectedSubpage) {
          setSelectedSubpage(subpages[0].id);
        }
      } catch (err) {
        console.error('Error fetching subpages:', err);
        setError('Failed to load subpages');
      }
    };

    fetchSubpages();
  }, []);

  // Fetch data from the Excel file for selected subpage
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSubpage) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/toc-kpi/subpage-data/${selectedSubpage}`);
        
        const { columns: apiColumns, data: apiData } = response.data;
        
        // Convert raw data to objects with column names
        const processedData = apiData.map(row => {
          const obj = {};
          apiColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });
        
        setData(processedData);
        
        // Extract unique values for filters - handle different possible column names
        const uniqueBrands = [...new Set(processedData.map(item => item.brand || item.Brand).filter(Boolean))].sort();
        
        setBrands(uniqueBrands);
        
        // Set predefined position options
        setPositions([
          { value: '', label: 'All Records' },
          { value: 'first_row', label: 'First Row' },
          { value: 'first_3_rows', label: 'First 3 Rows' },
          { value: 'first_5_rows', label: 'First 5 Rows' },
          { value: 'first_10_rows', label: 'First 10 Rows' }
        ]);
        
        // Set subpage details
        if (processedData.length > 0) {
          setSubpageDetails({
            filename: processedData[0].filename || 'Unknown',
            totalProducts: processedData.length,
            totalBanners: processedData.filter(item => item.component_type === 'banner').length,
            dateExtracted: processedData[0].extraction_date || 'Unknown'
          });
        }
        
      } catch (err) {
        console.error('Error fetching subpage data:', err);
        setError('Failed to load subpage data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSubpage]);

  // Filter data based on selected filters
  const filteredData = data.filter(item => {
    const itemBrand = item.brand || item.Brand;
    
    const matchesBrand = selectedBrand.length === 0 || selectedBrand.includes(itemBrand);
    
    // Handle position filtering based on 4 products per row
    let matchesPosition = true;
    if (selectedPosition) {
      const position = parseInt(item.position || item.Position) || 0;
      
      switch (selectedPosition) {
        case 'first_row':
          // First row: positions 1-4
          matchesPosition = position >= 1 && position <= 4;
          break;
        case 'first_3_rows':
          // First 3 rows: positions 1-12
          matchesPosition = position >= 1 && position <= 12;
          break;
        case 'first_5_rows':
          // First 5 rows: positions 1-20
          matchesPosition = position >= 1 && position <= 20;
          break;
        case 'first_10_rows':
          // First 10 rows: positions 1-40
          matchesPosition = position >= 1 && position <= 40;
          break;
        default:
          matchesPosition = true;
      }
    }
    
    return matchesBrand && matchesPosition;
  });

  // Calculate TOC metrics
  const calculateTOCMetrics = () => {
    if (!filteredData.length) return null;

    // Group by brand
    const brandCounts = {};

    filteredData.forEach(item => {
      const brand = item.brand || item.Brand || 'Unknown';
      
      // Brand counts
      brandCounts[brand] = (brandCounts[brand] || 0) + 1;
    });

    return {
      brandCounts,
      totalItems: filteredData.length
    };
  };

  const tocMetrics = calculateTOCMetrics();

  // Prepare chart data
  const prepareChartData = () => {
    if (!tocMetrics) return null;

    const brandLabels = Object.keys(tocMetrics.brandCounts);
    const brandData = Object.values(tocMetrics.brandCounts);
    const total = brandData.reduce((a, b) => a + b, 0);

    return {
      labels: brandLabels,
      datasets: [{
        label: `Brand Distribution${selectedBrand.length > 0 ? ` (${selectedBrand.join(', ')})` : ''}`,
        data: brandData,
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0'
        ],
        borderWidth: 2
      }]
    };
  };

  const chartData = prepareChartData();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2rem' }
            }}
          >
            Total Ownership KPI - Subpage Analysis
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mt: 0.5
            }}
          >
            Analyze Total Ownership Coefficient metrics for specific subpages
          </Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selectedSubpage) {
    return (
      <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2rem' }
            }}
          >
            Total Ownership KPI - Subpage Analysis
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mt: 0.5
            }}
          >
            Analyze Total Ownership Coefficient metrics for specific subpages
          </Typography>
        </Box>
        <Alert severity="info">Please select a subpage to view data.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '1400px', px: 3, py: 2 }}>
      {/* Header - Matching TOCView styling */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
            mb: 0.5,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.75rem', md: '2rem' }
          }}
        >
          Total Ownership KPI - Subpage Analysis
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            mt: 0.5
          }}
        >
          Analyze Total Ownership Coefficient metrics for specific subpages
        </Typography>
      </Box>

      {/* Subpage Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Subpage
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Subpage</InputLabel>
            <Select
              value={selectedSubpage}
              onChange={(e) => setSelectedSubpage(e.target.value)}
              label="Subpage"
            >
              {availableSubpages.map((subpage) => (
                <MenuItem key={subpage.id} value={subpage.id}>
                  {subpage.filename} ({subpage.total_products} products, {subpage.total_banners} banners)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
      
      {/* Filter Summary */}
      {(selectedBrand.length > 0 || selectedPosition) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Filters
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedBrand.length > 0 && (
                <Chip label={`Brands: ${selectedBrand.join(', ')}`} color="primary" />
              )}
              {selectedPosition && (
                <Chip label={`Position: ${positions.find(p => p.value === selectedPosition)?.label || selectedPosition}`} color="info" />
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      
      {subpageDetails && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" color="primary">
                  Subpage: {subpageDetails.filename}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">
                  <strong>Total Products:</strong> {subpageDetails.totalProducts}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">
                  <strong>Date Extracted:</strong> {subpageDetails.dateExtracted}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Brands</InputLabel>
                <Select
                  multiple
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  input={<OutlinedInput label="Brands" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      <Checkbox checked={selectedBrand.indexOf(brand) > -1} />
                      <ListItemText primary={brand} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={selectedPosition}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  label="Position"
                >
                  {positions.map((position) => (
                    <MenuItem key={position.value} value={position.value}>
                      {position.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TOC Metrics */}
      {tocMetrics && (
        <Grid container spacing={3}>
          {/* Brand Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Brand Distribution{selectedBrand.length > 0 ? ` - ${selectedBrand.join(', ')}` : ''}
                </Typography>
                {chartData && (
                  <Box height={300}>
                    <Doughnut data={chartData} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = ((value / total) * 100).toFixed(1);
                              return `${label} [${percentage}%]`;
                            }
                          }
                        },
                        datalabels: {
                          color: '#fff',
                          font: {
                            weight: 'bold',
                            size: 11
                          },
                          formatter: function(value, context) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100);
                            
                            // Only show label if percentage is greater than 5%
                            if (percentage > 5) {
                              const label = context.chart.data.labels[context.dataIndex];
                              return `${label}\n[${percentage.toFixed(1)}%]`;
                            }
                            return ''; // Return empty string for segments less than 5%
                          },
                          textAlign: 'center'
                        }
                      }
                    }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Brand Distribution Table */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Brand Distribution{selectedBrand.length > 0 ? ` - ${selectedBrand.join(', ')}` : ''}
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Brand</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Share (%)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(tocMetrics.brandCounts)
                        .sort(([,a], [,b]) => b - a)
                        .map(([brand, count]) => (
                          <TableRow key={brand}>
                            <TableCell>{brand}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                            <TableCell align="right">
                              {((count / tocMetrics.totalItems) * 100).toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Raw Data Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Raw Data ({filteredData.length} items)
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.slice(0, 50).map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.position || item.Position || 'N/A'}</TableCell>
                    <TableCell>{item.product_name || item.productName || item.title || 'N/A'}</TableCell>
                    <TableCell>{item.brand || item.Brand || 'N/A'}</TableCell>
                    <TableCell>
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      ) : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TOKSubpageView; 